import { useState } from "react"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Stack,
  CircularProgress,
} from "@mui/material"
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
} from "@mui/icons-material"
import { useApiMutation, useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"


///
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
///


function SearchClientPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [hasSearched, setHasSearched] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  
  // Menu and Dialog states
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedClient, setSelectedClient] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)

  const { mutate: searchClientMutation, data, isError, isPending, error } = useApiMutation(
    "/clients/search",
    "POST"
  )

  const { data:{jobs: allClientJobs} = {}, isLoading: jobsLoading } = useApiQuery(
    ["all-client-jobs", selectedClient?._id],
    selectedClient ? `/clients/${selectedClient._id}/jobs` : null,
    { enabled: !!selectedClient }
  )

  // const ongoingJobs = allClientJobs?.jobs || []
  const searchResults = data?.clients || []

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setHasSearched(true)
    setPage(0)

    searchClientMutation({ searchTerm: searchQuery })
  }

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch()
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuClick = (event, client) => {
    setAnchorEl(event.currentTarget)
    setSelectedClient(client)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleViewDetails = () => {
    setViewDialogOpen(true)
    handleMenuClose()
  }

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setSelectedClient(null)
  }

  const formatCurrency = (amount) => {
    const numAmount = Number(amount) || 0
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(numAmount)
  }

  const paginatedResults = searchResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

    ///
    const ledgerRef = useRef();
    ///
    const handlePrintLedger = useReactToPrint({
    contentRef: ledgerRef,
    documentTitle: `Client_Ledger_${selectedClient?.name || "Client"}`,
    });
    const handleExportLedgerExcel = () => {
    if (!allClientJobs || allClientJobs.length === 0) return;
  
    const worksheetData = [
      ["Job ID", "Job Type", "Status", "Fee Status", "Settled By", "Assigned To", "Fees", "Created At"]
    ];
  
    allClientJobs.forEach(job => {
      worksheetData.push([
        job.JobId,
        job.jobType,
        job.status,
        job.feeStatus,
        job.settledBy,
        job.assignedTo,
        job.fees,
        new Date(job.createdAt).toLocaleString(),
      ]);
    });
  
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");
  
    XLSX.writeFile(workbook, `Client_Ledger_${selectedClient?.name || "Client"}.xlsx`);
    };
    ///

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Search Clients
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Search for clients by name, phone number, or PAN.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Enter client name, phone, or PAN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isPending || !searchQuery.trim()}
            sx={{ minWidth: 120 }}
          >
            {isPending ? "Searching..." : "Search"}
          </Button>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error searching clients: {error?.response?.data?.message || error?.message || "Unknown error"}
        </Alert>
      )}

      {hasSearched && !isPending && (
        <Box>
          {searchResults.length === 0 ? (
            <Alert severity="info">No clients found matching your search criteria.</Alert>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Search Results ({searchResults.length} found)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>PAN</TableCell>
                      <TableCell>Constitution</TableCell>
                      <TableCell>New Jobs</TableCell>
                      <TableCell>Processing Jobs</TableCell>
                      <TableCell>Completed Jobs</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedResults.map((client) => (
                      <TableRow key={client._id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {client.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {client.tradeName}
                          </Typography>
                        </TableCell>
                        <TableCell>{client.phoneNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {client.panNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={client.constitution} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={client?.newJobCount || 0} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={client?.processingJobCount || 0} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={client?.completedJobCount || 0} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(e) => handleMenuClick(e, client)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={searchResults.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Client Details
            </Typography>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedClient && (
            <Box>
              {/* Client Information */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Client Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedClient.name}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Trade Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedClient.tradeName || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Phone Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedClient.phoneNumber}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          PAN Number
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                          {selectedClient.panNumber}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Constitution
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedClient.constitution}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          GSTIN Number
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedClient.gstinNumber || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <AccountBalanceIcon fontSize="small" color="success" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Current Balance
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {formatCurrency(selectedClient.balance || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={selectedClient.status || "Active"}
                            color={selectedClient.status === "Active" ? "success" : "default"}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>

                  {selectedClient.address && (
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Address
                          </Typography>
                          <Typography variant="body1">{selectedClient.address}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}

                  {/* <Grid item xs={12}>
                    <Divider />
                  </Grid> */}

                  {/* <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      New Jobs
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="info.main">
                      {selectedClient.newJobCount || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      Processing Jobs
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                      {selectedClient.processingJobCount || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary">
                      Completed Jobs
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {selectedClient.completedJobCount || 0}
                    </Typography>
                  </Grid> */}

                  {/* {selectedClient.createdAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        Created At
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedClient.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                  )} */}
                </Grid>
              </Paper>

              {/* Ongoing Jobs */}
              {/* Client Ledger Section */}
              <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={600}>
                    Client Jobs ({allClientJobs?.length || 0} jobs)
                  </Typography>

                  {/* <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={handlePrintLedger}
                    >
                      Print PDF
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={handleExportLedgerExcel}
                    >
                      Export Excel
                    </Button>
                  </Stack> */}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <div ref={ledgerRef}>
                  {jobsLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : allClientJobs && allClientJobs.length > 0 ? (
                    <>

                      {/* Pagination state */}
                      {(() => {
                        if (!window._clientLedgerPageState) {
                          window._clientLedgerPageState = { page: 0, rows: 20 };
                        }
                      })()}

                    <TableContainer sx={{ maxHeight: 350 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Job ID</strong></TableCell>
                            <TableCell><strong>Job Type</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Fee Status</strong></TableCell>
                            <TableCell><strong>Settled By</strong></TableCell>
                            <TableCell><strong>Assigned To</strong></TableCell>
                            <TableCell><strong>Fees</strong></TableCell>
                            <TableCell><strong>Created At</strong></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {allClientJobs
                            .slice(
                              window._clientLedgerPageState.page * window._clientLedgerPageState.rows,
                              window._clientLedgerPageState.page * window._clientLedgerPageState.rows +
                                window._clientLedgerPageState.rows
                            )
                            .map((job) => (
                              <TableRow key={job.id} hover>
                                <TableCell>{job.JobId}</TableCell>
                                <TableCell>{job.jobType}</TableCell>

                                <TableCell>
                                  <Chip
                                    label={job.status}
                                    size="small"
                                    color={
                                      job.status === "completed"
                                        ? "success"
                                        : job.status === "processing"
                                        ? "primary"
                                        : job.status === "cancelled"
                                        ? "error"
                                        : "default"
                                    }
                                  />
              </TableCell>

              <TableCell>
                <Chip
                  label={job.feeStatus}
                  size="small"
                  color={job.feeStatus === "settled" ? "success" : "warning"}
                />
              </TableCell>

              <TableCell>{job.settledBy}</TableCell>
              <TableCell>{job.assignedTo}</TableCell>

              <TableCell>{job.fees}</TableCell>

              <TableCell>
                {new Date(job.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  </TableContainer>

  <TablePagination
    component="div"
    rowsPerPageOptions={[20, 30, 40]}
    count={allClientJobs.length}
    rowsPerPage={window._clientLedgerPageState.rows}
    page={window._clientLedgerPageState.page}
    onPageChange={(e, newPage) => {
      window._clientLedgerPageState.page = newPage;
    }}
    onRowsPerPageChange={(e) => {
      window._clientLedgerPageState.rows = parseInt(e.target.value, 10);
      window._clientLedgerPageState.page = 0;
    }}
  />
</>
) : (
<Box textAlign="center" py={4}>
  <WorkIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
  <Typography variant="h6" color="textSecondary">
    No jobs to display
  </Typography>
</Box>
)}
</div>
</Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/add-job?clientId=${selectedClient?._id}`)}
          >
            Create Job
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SearchClientPage